/**
 * MODULE: domain/sales-target-management/frontend/components/SalesTargetIntegrationBadge.tsx
 * PURPOSE: Visual indicator for integration-managed or locked quotas
 */

import { Badge, Tooltip } from 'flowbite-react';
import { HiOutlineLockClosed, HiOutlineCloud } from 'react-icons/hi';
import type { SalesTarget } from '../../contracts/sales-targets.types';
import {
  formatSalesTargetSourceLabel,
  isSalesTargetLocked,
  salesTargetIntegrationHint,
} from '../utils/salesTargetIntegrationUtils';

export function SalesTargetIntegrationBadge({ target }: { target: SalesTarget }) {
  const locked = isSalesTargetLocked(target);
  const source = target.source_system ?? 'brazn';
  const hint = salesTargetIntegrationHint(target);

  if (!locked && source === 'brazn') {
    return null;
  }

  const label = locked
    ? `Locked · ${formatSalesTargetSourceLabel(source)}`
    : formatSalesTargetSourceLabel(source);

  const badge = (
    <Badge
      color={locked ? 'warning' : 'info'}
      size="sm"
      className="inline-flex items-center gap-1"
      data-testid={locked ? 'sales-target-locked-badge' : 'sales-target-source-badge'}
    >
      {locked ? (
        <HiOutlineLockClosed className="h-3 w-3" />
      ) : (
        <HiOutlineCloud className="h-3 w-3" />
      )}
      {label}
    </Badge>
  );

  if (!hint) return badge;

  return (
    <Tooltip content={hint} style="dark">
      {badge}
    </Tooltip>
  );
}
